import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiUser, FiLock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import LogoImg from "../assets/images/obsessed.png";
import { login } from "../store/slices/auth/asyncThunks";
import { selectLoginPending } from "../store/slices/auth/slice";

const LoginPage = () => {
  const navigate = useNavigate();
  const loading = useSelector(selectLoginPending);
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleLogin = () => {
    if (loading) return;
    if (!email.trim() || !password) {
      setError("Unesi email i lozinku.");
      return;
    }
    setError("");
    dispatch(login({ email, password }))
      .unwrap()
      .then(() => {
        navigate("/kontrolna-tabla");
      })
      .catch((err) => setError(err.message));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <Container>
      <Kanji>管理</Kanji>

      <Card
        as={motion.div}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 0.72, 0.18, 1] }}
      >
        <Logo src={LogoImg} alt="OBSESSED" onClick={() => navigate("/")} />

        <Title>
          Administracija <span>管理</span>
        </Title>
        <Sub>Prijavi se da nastaviš</Sub>

        <Field>
          <FieldLabel>E-mail adresa</FieldLabel>
          <InputWrapper $error={!!error}>
            <FiUser size={16} />
            <Input
              placeholder="admin@obsessed.rs"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKeyDown}
              autoComplete="username"
            />
          </InputWrapper>
        </Field>

        <Field>
          <FieldLabel>Lozinka</FieldLabel>
          <InputWrapper $error={!!error}>
            <FiLock size={16} />
            <Input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={onKeyDown}
              autoComplete="current-password"
            />
          </InputWrapper>
        </Field>

        {error && <Error>{error}</Error>}

        <LoginBtn $disabled={loading} onClick={handleLogin}>
          {loading ? (
            <>
              Prijavljivanje…
              <l-line-spinner
                size="16"
                stroke="2"
                speed="1"
                color="#fe5f55"
                style={{ position: "absolute", right: "2rem" }}
              />
            </>
          ) : (
            <>
              Prijavi se <i>→</i>
            </>
          )}
        </LoginBtn>

        <BackLink onClick={() => navigate("/")}>← Nazad na sajt</BackLink>
      </Card>
    </Container>
  );
};

export default LoginPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100svh;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: radial-gradient(
    ellipse 80% 60% at 50% 0%,
    #15151a 0%,
    var(--bg0, #0a0a0c) 65%
  );
  position: relative;
  overflow: hidden;
`;

const Kanji = styled.div`
  position: absolute;
  right: clamp(-4rem, 4vw, 10rem);
  top: 50%;
  transform: translateY(-50%);
  font-family: var(--font-kanji);
  font-size: clamp(22rem, 40vh, 40rem);
  line-height: 0.9;
  writing-mode: vertical-rl;
  color: rgba(254, 95, 85, 0.05);
  -webkit-text-stroke: 2px rgba(254, 95, 85, 0.14);
  user-select: none;
  pointer-events: none;
`;

const Card = styled.div`
  width: 100%;
  max-width: 42rem;
  padding: 3.6rem;
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.6rem;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
`;

const Logo = styled.img`
  height: 3rem;
  width: auto;
  align-self: flex-start;
  margin-bottom: 2.6rem;
  cursor: pointer;
`;

const Title = styled.h2`
  color: var(--text, #f4f2ee);
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 2.8rem;
  line-height: 1;

  span {
    font-family: var(--font-kanji);
    font-size: 0.55em;
    color: var(--red, #fe5f55);
    vertical-align: middle;
    margin-left: 0.6rem;
  }
`;

const Sub = styled.p`
  font-size: 1.3rem;
  font-family: "Poppins-Regular";
  color: var(--muted, #8e8c94);
  margin: 0.8rem 0 2.6rem;
`;

const Field = styled.div`
  margin-bottom: 1.6rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 1.1rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-family: "Poppins-Medium";
  color: var(--muted, #8e8c94);
  margin-bottom: 0.7rem;
`;

const InputWrapper = styled.div`
  background: var(--bg1, #101013);
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 1.4rem;
  height: 4.6rem;
  border-radius: 0.4rem;
  border: 1px solid
    ${(props) =>
      props.$error
        ? "rgba(254, 95, 85, 0.6)"
        : "var(--line2, rgba(255,255,255,0.14))"};
  color: var(--muted, #8e8c94);
  transition: border-color 0.25s;

  &:focus-within {
    border-color: var(--red, #fe5f55);

    svg {
      color: var(--red, #fe5f55);
    }
  }
`;

const Input = styled.input`
  background: transparent;
  border: none;
  color: var(--text, #f4f2ee);
  font-size: 1.4rem;
  flex: 1;
  height: 100%;
  outline: none;
  font-family: "Poppins-Regular";

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }
`;

const Error = styled.span`
  font-size: 1.25rem;
  font-family: "Poppins-Medium";
  color: var(--red, #fe5f55);
  background: rgba(254, 95, 85, 0.08);
  border: 1px solid rgba(254, 95, 85, 0.3);
  border-radius: 0.4rem;
  padding: 1rem 1.4rem;
  margin-bottom: 1.6rem;
`;

const LoginBtn = styled.button`
  padding: 1.5rem;
  font-size: 1.3rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  border-radius: 0.4rem;
  cursor: ${(props) => (props.$disabled ? "wait" : "pointer")};
  outline: none;
  transition: background 0.3s var(--ease, ease);

  position: relative;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 1rem;
  justify-content: center;

  color: ${(props) =>
    props.$disabled ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  background: ${(props) =>
    props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  border: 1px solid
    ${(props) =>
      props.$disabled ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};

  i {
    font-style: normal;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover {
    background: ${(props) =>
      props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};

    i {
      transform: translateX(0.4rem);
    }
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: var(--muted2, #5c5a63);
  font-size: 1.2rem;
  font-family: "Poppins-Medium";
  letter-spacing: 0.06em;
  cursor: pointer;
  margin-top: 1.8rem;
  align-self: center;
  transition: color 0.25s;

  &:hover {
    color: var(--red, #fe5f55);
  }
`;
